/**
 * useBookingState — Centralized state management for Booking Widget
 *
 * Flow:
 *   Login → Verify → Date (1/5) → Time (2/5) → Players (3/5) → Review (4/5) → Pay (5/5) → Confirmation
 *
 * Login, Verify, and Confirmation are outside the numbered progress bar.
 * Currently uses mock data; will be swapped to real APIs in Phase 3.
 */

import { useState, useCallback, useMemo } from 'preact/hooks'
import { analytics } from '../analytics/events'
import type { TimeSlot, PlayerInfo, BookingAccess } from '../types'
import {
  mockClub,
  mock7MBClub,
  mockCourses,
  mock7MBCourses,
  getMockTeeTimes,
  getMockAvailableDates,
  getMockPriceBreakdown,
  mockPrimaryPlayer,
  mockBookingConfirmation,
  type MockCourse,
  type MockPriceBreakdown,
} from '../mock-data'

// ─── Types ───────────────────────────────────────────────────────────────────

export type BookingStepV2 =
  | 'login'
  | 'verify'
  | 'golfer-details'
  | 'date'
  | 'time'
  | 'players'
  | 'summary'
  | 'payment'
  | 'confirmation'

const FULL_STEP_ORDER_WITH_VERIFY: BookingStepV2[] = [
  'login',
  'verify',
  'date',
  'time',
  'players',
  'summary',
  'payment',
  'confirmation',
]

const FULL_STEP_ORDER_WITHOUT_VERIFY: BookingStepV2[] = [
  'login',
  'golfer-details',
  'date',
  'time',
  'players',
  'summary',
  'payment',
  'confirmation',
]

export const BOOKING_STEPS: BookingStepV2[] = [
  'date',
  'time',
  'players',
  'summary',
  'payment',
]

export const STEP_LABELS: Record<BookingStepV2, string> = {
  login: 'Sign In',
  verify: 'Verify',
  'golfer-details': 'Details',
  date: 'Date',
  time: 'Time',
  players: 'Players',
  summary: 'Review',
  payment: 'Pay',
  confirmation: 'Confirmed',
}

export type AuthMethod = 'email' | 'google' | 'apple' | 'meta'

export interface AuthState {
  isAuthenticated: boolean
  email: string | null
  method: AuthMethod | null
}

export interface VerifyFormData {
  firstName: string
  lastName: string
  handicap: string
  handicapSystem: string
  homeClubName: string
  homeClubCountry: string
}

export interface VerifyState {
  isVerified: boolean
  profile: VerifyFormData | null
}

export interface GolferDetailsData {
  firstName: string
  lastName: string
  email: string
  phone: string
  handicap: string
  homeClubName: string
  golfId?: string
}

export interface LoadingState {
  isLoading: boolean
  message: string | null
}

export interface ToastState {
  id: string
  type: 'success' | 'error' | 'info'
  message: string
}

export interface BookingStateV2 {
  currentStep: BookingStepV2
  stepIndex: number
  bookingStepIndex: number
  stepLabel: string
  totalBookingSteps: number
  canGoBack: boolean
  canGoForward: boolean
  direction: 'forward' | 'backward'
  isBookingStep: boolean

  auth: AuthState
  verify: VerifyState

  club: typeof mockClub
  courses: MockCourse[]
  selectedCourseId: string | null

  availableDates: Date[]
  selectedDate: Date | null
  timeSlots: TimeSlot[]
  selectedTimeSlot: TimeSlot | null

  playerCount: number
  players: PlayerInfo[]

  priceBreakdown: MockPriceBreakdown | null
  confirmation: typeof mockBookingConfirmation | null

  theme: 'light' | 'dark'
  loading: LoadingState
  toasts: ToastState[]

  goToStep: (step: BookingStepV2) => void
  goNext: () => void
  goBack: () => void
  login: (method: AuthMethod, email?: string) => void
  submitGolferDetails: (data: GolferDetailsData) => void
  verifyProfile: (data: VerifyFormData) => void
  selectDate: (date: Date) => void
  selectCourse: (courseId: string) => void
  selectTimeSlot: (slot: TimeSlot) => void
  setPlayerCount: (count: number) => void
  updatePlayer: (index: number, player: PlayerInfo) => void
  addPlayer: () => void
  removePlayer: (index: number) => void
  confirmBooking: () => void
  reset: () => void
  addToast: (type: 'success' | 'error' | 'info', message: string) => void
  dismissToast: (id: string) => void
}

// ─── Hook ────────────────────────────────────────────────────────────────────

interface UseBookingStateOptions {
  clubId?: string
  theme?: 'light' | 'dark'
  skipVerification?: boolean
  requirePhone?: boolean
  onStepChange?: (step: BookingStepV2) => void
  onBookingComplete?: (bookingId: string) => void
}

export function useBookingState(options: UseBookingStateOptions = {}): BookingStateV2 {
  const { theme = 'light', skipVerification = false, onStepChange, onBookingComplete, clubId } = options

  // Select club data based on clubId
  const is7MB = clubId === '7mb' || clubId === '7mile-beach'
  const clubData = is7MB ? mock7MBClub : mockClub
  const coursesData = is7MB ? mock7MBCourses : mockCourses

  // Dynamic step order based on skipVerification
  const FULL_STEP_ORDER = skipVerification ? FULL_STEP_ORDER_WITHOUT_VERIFY : FULL_STEP_ORDER_WITH_VERIFY

  const [currentStep, setCurrentStep] = useState<BookingStepV2>('login')
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward')

  const [auth, setAuth] = useState<AuthState>({
    isAuthenticated: false,
    email: null,
    method: null,
  })

  const [verifyState, setVerifyState] = useState<VerifyState>({
    isVerified: false,
    profile: null,
  })

  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null)
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null)
  const [playerCount, setPlayerCountState] = useState(1)
  const [players, setPlayers] = useState<PlayerInfo[]>([{ ...mockPrimaryPlayer }])
  const [confirmation, setConfirmation] = useState<typeof mockBookingConfirmation | null>(null)
  const [loading, setLoading] = useState<LoadingState>({ isLoading: false, message: null })
  const [toasts, setToasts] = useState<ToastState[]>([])

  // ─── Toast Management ──────────────────────────────────────────────────
  const addToast = useCallback((type: 'success' | 'error' | 'info', message: string) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    setToasts(prev => [...prev, { id, type, message }])
  }, [])

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  // ─── Derived State ───────────────────────────────────────────────────────

  const stepIndex = FULL_STEP_ORDER.indexOf(currentStep)
  // If verify is skipped and we're somehow on verify, jump to date
  if (currentStep === 'verify' && skipVerification) {
    setCurrentStep('date')
  }
  const bookingStepIndex = BOOKING_STEPS.indexOf(currentStep)
  const isBookingStep = bookingStepIndex >= 0
  const stepLabel = STEP_LABELS[currentStep]
  const totalBookingSteps = BOOKING_STEPS.length

  const availableDates = useMemo(() => getMockAvailableDates(), [])

  const timeSlots = useMemo(() => {
    if (!selectedDate) return []
    return getMockTeeTimes(selectedDate, selectedCourseId || undefined, coursesData)
  }, [selectedDate, selectedCourseId])

  const priceBreakdown = useMemo(() => {
    if (players.length === 0 || !selectedTimeSlot) return null
    return getMockPriceBreakdown(players, selectedTimeSlot)
  }, [players, selectedTimeSlot])

  const canGoBack = (() => {
    if (currentStep === 'login') return false
    if (currentStep === 'verify') return false
    if (currentStep === 'confirmation') return false
    if (currentStep === 'date') return false
    return true
  })()

  const canGoForward = stepIndex < FULL_STEP_ORDER.length - 1

  // ─── Navigation ──────────────────────────────────────────────────────────

  const goToStep = useCallback((step: BookingStepV2) => {
    const newIndex = FULL_STEP_ORDER.indexOf(step)
    const oldIndex = FULL_STEP_ORDER.indexOf(currentStep)
    setDirection(newIndex > oldIndex ? 'forward' : 'backward')
    setCurrentStep(step)
    onStepChange?.(step)
    analytics.stepChanged(step, newIndex)
  }, [currentStep, onStepChange])

  const goNext = useCallback(() => {
    const nextIndex = FULL_STEP_ORDER.indexOf(currentStep) + 1
    if (nextIndex < FULL_STEP_ORDER.length) {
      setDirection('forward')
      const nextStep = FULL_STEP_ORDER[nextIndex]
      setCurrentStep(nextStep)
      onStepChange?.(nextStep)
      analytics.stepChanged(nextStep, nextIndex)
      if (nextStep === 'summary') {
        analytics.bookingStarted()
      }
    }
  }, [currentStep, onStepChange])

  const goBack = useCallback(() => {
    if (isBookingStep && bookingStepIndex > 0) {
      const prevStep = BOOKING_STEPS[bookingStepIndex - 1]
      setDirection('backward')
      setCurrentStep(prevStep)
      onStepChange?.(prevStep)
      analytics.stepChanged(prevStep, FULL_STEP_ORDER.indexOf(prevStep))
    }
  }, [isBookingStep, bookingStepIndex, onStepChange])

  // ─── Auth ─────────────────────────────────────────────────────────────────

  const login = useCallback((method: AuthMethod, email?: string) => {
    setAuth({
      isAuthenticated: true,
      email: email || `user@${method}.com`,
      method,
    })
    setDirection('forward')
    if (skipVerification) {
      // Go to golfer details step instead of verify
      setCurrentStep('golfer-details')
      onStepChange?.('golfer-details')
    } else {
      setCurrentStep('verify')
      onStepChange?.('verify')
    }
  }, [onStepChange, skipVerification])

  // ─── Golfer Details ─────────────────────────────────────────────────────

  const submitGolferDetails = useCallback((data: GolferDetailsData) => {
    setPlayers(prev => {
      const updated = [...prev]
      updated[0] = {
        ...updated[0],
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email || auth.email || updated[0].email,
        phone: data.phone || undefined,
        handicap: data.handicap || undefined,
        homeClub: {
          ...updated[0].homeClub,
          name: data.homeClubName || '',
        },
      }
      return updated
    })
    setDirection('forward')
    setCurrentStep('date')
    onStepChange?.('date')
  }, [auth.email, onStepChange])

  // ─── Verification ─────────────────────────────────────────────────────────

  const verifyProfile = useCallback((data: VerifyFormData) => {
    setVerifyState({
      isVerified: true,
      profile: data,
    })
    setPlayers(prev => {
      const updated = [...prev]
      updated[0] = {
        ...updated[0],
        firstName: data.firstName,
        lastName: data.lastName,
        email: auth.email || updated[0].email,
        homeClub: {
          name: data.homeClubName,
          country: data.homeClubCountry,
          address: '',
        },
      }
      return updated
    })
    setDirection('forward')
    setCurrentStep('date')
    onStepChange?.('date')
  }, [auth.email, onStepChange])

  // ─── Date Selection ──────────────────────────────────────────────────────

  const selectDate = useCallback((date: Date) => {
    setSelectedDate(date)
    setSelectedTimeSlot(null)
    analytics.dateSelected(date.toISOString())
  }, [])

  const selectCourse = useCallback((courseId: string) => {
    setSelectedCourseId(courseId)
    setSelectedTimeSlot(null)
  }, [])

  const selectTimeSlot = useCallback((slot: TimeSlot) => {
    setSelectedTimeSlot(slot)
    analytics.timeSelected(slot.time, slot.domesticPrice)
  }, [])

  // ─── Player Management ───────────────────────────────────────────────────

  const setPlayerCount = useCallback((count: number) => {
    setPlayerCountState(count)
    setPlayers(prev => {
      if (count > prev.length) {
        const newPlayers = [...prev]
        for (let i = prev.length; i < count; i++) {
          newPlayers.push({
            id: `player-${i + 1}`,
            firstName: '',
            lastName: '',
            email: '',
            title: '',
            homeClub: {
              name: '',
              country: '',
              address: '',
            },
            isPrimary: false,
            bookingOptions: {
              caddie: false,
              clubHire: false,
              cart: false,
            },
          })
        }
        return newPlayers
      } else {
        return prev.slice(0, Math.max(count, 1))
      }
    })
  }, [])

  const updatePlayer = useCallback((index: number, player: PlayerInfo) => {
    setPlayers(prev => {
      const updated = [...prev]
      updated[index] = player
      return updated
    })
  }, [])

  const addPlayer = useCallback(() => {
    const maxPlayers = selectedTimeSlot?.maxPlayers || 4
    setPlayers(prev => {
      if (prev.length >= maxPlayers) return prev
      return [
        ...prev,
        {
          id: `player-${Date.now()}`,
          firstName: '',
          lastName: '',
          email: '',
          title: '',
          homeClub: {
            name: '',
            country: '',
            address: '',
          },
          isPrimary: false,
          bookingOptions: {
            caddie: false,
            clubHire: false,
            cart: false,
          },
        },
      ]
    })
    setPlayerCountState(prev => {
      const maxP = selectedTimeSlot?.maxPlayers || 4
      if (prev >= maxP) return prev
      return prev + 1
    })
  }, [selectedTimeSlot])

  const removePlayer = useCallback((index: number) => {
    if (index === 0) return
    setPlayerCountState(prev => prev - 1)
    setPlayers(prev => prev.filter((_, i) => i !== index))
  }, [])

  // ─── Booking Confirmation ────────────────────────────────────────────────

  const confirmBooking = useCallback(() => {
    setLoading({ isLoading: true, message: 'Confirming booking...' })
    setTimeout(() => {
      setConfirmation(mockBookingConfirmation)
      setLoading({ isLoading: false, message: null })
      setDirection('forward')
      setCurrentStep('confirmation')
      onStepChange?.('confirmation')
      onBookingComplete?.(mockBookingConfirmation.bookingId)
      analytics.bookingConfirmed(mockBookingConfirmation.bookingId)
      addToast('success', 'Booking confirmed successfully!')
    }, 1200)
  }, [onStepChange, onBookingComplete, addToast])

  // ─── Reset ───────────────────────────────────────────────────────────────

  const reset = useCallback(() => {
    setCurrentStep('login')
    setDirection('forward')
    setAuth({ isAuthenticated: false, email: null, method: null })
    setVerifyState({ isVerified: false, profile: null })
    setSelectedDate(null)
    setSelectedCourseId(null)
    setSelectedTimeSlot(null)
    setPlayerCountState(1)
    setPlayers([{ ...mockPrimaryPlayer }])
    setConfirmation(null)
  }, [])

  // ─── Return ──────────────────────────────────────────────────────────────

  return {
    currentStep,
    stepIndex,
    bookingStepIndex,
    isBookingStep,
    stepLabel,
    totalBookingSteps,
    canGoBack,
    canGoForward,
    direction,

    auth,
    verify: verifyState,

    club: clubData,
    courses: coursesData,
    selectedCourseId,

    availableDates,
    selectedDate,
    timeSlots,
    selectedTimeSlot,

    playerCount,
    players,

    priceBreakdown,
    confirmation,

    theme,
    loading,
    toasts,

    goToStep,
    goNext,
    goBack,
    login,
    submitGolferDetails,
    verifyProfile,
    selectDate,
    selectCourse,
    selectTimeSlot,
    setPlayerCount,
    updatePlayer,
    addPlayer,
    removePlayer,
    confirmBooking,
    reset,
    addToast,
    dismissToast,
  }
}
